import React, { useState } from 'react';
import {
  CcfHeader,
  CcfSettingsIcon,
  CcfTabs,
  useTranslator,
} from '@nice-ccf/ui-controls';
import {
  CcfAgentSkills,
  CcfAppHealth,
  CcfAVNotifications,
  CcfDisplayAndAccessability,
  CcfLoginVoicePreferanceTab,
  getApplicationDirection,
} from '@nice-ccf/ui-components';
import {
  Box,
  Direction,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  useMediaQuery,
} from '@mui/material';
import { makeStyles } from '@mui/styles';

import { Theme, useTheme } from '@mui/material';

import { useSelector } from 'react-redux';
import ccfAppSettingsStyles from './ccf-app-settings.styles';
import CcfReportIssue from './ccf-report-issue/ccf-report-issue';
import createStyles from '@mui/styles/createStyles';

export enum SettingsDropdownValues {
  AV_NOTIFICATIONS = 'A/V Notifications',
  DISPLAY = 'Display & Keyboard',
  VOICE = 'Login & Voice Preferences',
  INFORMATION = 'Information',
  AGENT_SKILLS = 'Agent Skills',
  REPORT_ISSUE = 'Report Issue',
}

interface SettingsTab {
  element: React.ReactElement;
  label: string;
  isActive: boolean;
}

/* eslint-disable-next-line */
export interface CcfAppSettingsProps {}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    interactionSearchHeader: {
      background: `${theme.palette.background.level1} 0% 0% no-repeat padding-box`,
      borderRadius: '5px 5px 0px 0px',
      opacity: 1,
      display: 'flex',
      alignItems: 'flex-start',
      flexDirection: 'column',
      paddingTop: '7px',
      paddingBottom: '10px',
      paddingLeft: '5px',
      marginTop: '0px !important',
      '& section': {
        marginBottom: '0px !important',
        alignItems: 'center',
        width: '100%',
      },
      '& > div': {
        marginTop: '0px !important',
      },
      '& label': {
        color: theme.palette.text.secondary,
        marginLeft: '4px !important',
        marginTop: '0px !important',
        fontWeight: 'bold',
        lineHeight: '1',
      },
      '& svg': {
        verticalAlign: 'middle',
      },
    },
  })
);
/**
 * used to show App Settings static screen
 * @param props -CcfAppSettingsProps
 * @example `<CcfAppSettings inboxCardState={inboxCardState} />`
 */
export function CcfAppSettings(props: CcfAppSettingsProps) {
  const classes = useStyles();
  const theme = useTheme();
  const appDirection: Direction = useSelector(getApplicationDirection);
  const isSmView = useMediaQuery(theme.breakpoints.down('xl'));
  const [tab, setTab] = React.useState(0);
  const [translate] = useTranslator();
  const styles = ccfAppSettingsStyles(theme);
  const [settingsValue, setSettingsValue] = useState(
    SettingsDropdownValues.VOICE
  );
  const settingsTabs: SettingsTab[] = [
    {
      element: <CcfLoginVoicePreferanceTab />,
      isActive: true,
      label: translate('loginAndVoicePreference'),
    },
    {
      element: <CcfAVNotifications />,
      isActive: true,
      label: translate('avNotifications'),
    },
    {
      element: <CcfDisplayAndAccessability />,
      isActive: true,
      label: translate('displayAndKeyboard'),
    },
    {
      element: <CcfAppHealth />,
      isActive: true,
      label: translate('information'),
    },
    {
      element: <CcfAgentSkills />,
      isActive: true,
      label: translate('agentSkills'),
    },
    {
      element: <CcfReportIssue />,
      isActive: true,
      label: translate('reportIssue'),
    },
  ];
  const activeTabs = settingsTabs.filter((tab) => tab.isActive);

  /**
   *
   * @param e - event
   * @example handleChange
   */
  const handleChange = (event: SelectChangeEvent) => {
    setSettingsValue(event.target.value as SettingsDropdownValues);
  };

  /**
   *
   * @param e - event
   * @example handleTabChange
   */
  const handleTabChange = (newValue: number) => {
    setTab(newValue);
  };

  return (
    <Box sx={styles.FullViewSettingsCard}>
      {isSmView ? (
        <>
          <Box sx={styles.settingsHeader}>
            <CcfHeader
              headerText={translate('settings')}
              RightIcon
              showDragIcon
              direction={appDirection}
            />
          </Box>
          <Paper square elevation={0} sx={styles.settingsContainer}>
            <div style={styles.menuTab}>
              <Select
                labelId="settings-select-label"
                id="settings-select"
                value={settingsValue}
                onChange={handleChange}
                sx={{
                  width: { xs: '100%', lg: '255px' },
                  height: '40px',
                  padding: '0px',
                }}
              >
                <MenuItem value={SettingsDropdownValues.VOICE}>
                  {translate('loginAndVoicePreference')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.AV_NOTIFICATIONS}>
                  {translate('avNotifications')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.DISPLAY}>
                  {translate('displayAndKeyboard')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.INFORMATION}>
                  {translate('information')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.AGENT_SKILLS}>
                  {translate('agentSkills')}
                </MenuItem>
                <MenuItem value={SettingsDropdownValues.REPORT_ISSUE}>
                  {translate('reportIssue')}
                </MenuItem>
              </Select>
            </div>
            {settingsValue === SettingsDropdownValues.VOICE && (
              <CcfLoginVoicePreferanceTab />
            )}
            {settingsValue === SettingsDropdownValues.AV_NOTIFICATIONS && (
              <CcfAVNotifications />
            )}
            {settingsValue === SettingsDropdownValues.DISPLAY && (
              <CcfDisplayAndAccessability />
            )}
            {settingsValue === SettingsDropdownValues.INFORMATION && (
              <CcfAppHealth />
            )}
            {settingsValue === SettingsDropdownValues.AGENT_SKILLS && (
              <CcfAgentSkills />
            )}
            {settingsValue === SettingsDropdownValues.REPORT_ISSUE && (
              <CcfReportIssue />
            )}
          </Paper>
        </>
      ) : (
        <div className={classes.interactionSearchHeader}>
          <CcfHeader
            LeftIcon={
              <CcfSettingsIcon
                color="primary"
                viewBox="-2 -2 23 23"
                fontSize="small"
              />
            }
            headerText={translate('settings')}
            RightIcon
            direction={appDirection}
          />
          <CcfTabs
            currentTab={tab}
            variant={CcfTabs.Variant.FULL_WIDTH}
            setCurrentTab={handleTabChange}
            bottomBorder={true}
          >
            {activeTabs.map((tab, index) => (
              <CcfTabs.TabPanel key={index} label={tab.label}>
                <Box sx={styles.container}>{tab.element}</Box>
              </CcfTabs.TabPanel>
            ))}
          </CcfTabs>
        </div>
      )}
    </Box>
  );
}

export default CcfAppSettings;
